import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
// Temporarily removing date-fns dependency to fix build issues
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { fr } from 'date-fns/locale';

// Composants
// Composants
import LoginPage from './Commun/pages/LoginPage';
import DashboardPage from './Planning/pages/DashboardPage';
import ParametersPage from './Planning/pages/ParametersPage';
import ProfilPage from './Planning/pages/ProfilPage';
import SecurityWebPage from './Commun/pages/SecurityWeb/SecurityWebPage';
import Layout from './Commun/components/Layout';
import { AuthProvider, useAuth } from './Commun/contexts/AuthContext';
import { NotificationProvider } from './Commun/contexts/NotificationContext';
import { LanguageProvider } from './Commun/contexts/LanguageContext';
import { TabProvider, useTabContext } from './Commun/contexts/TabContext';

import { ZoomButtonTest } from './Commun/components/ZoomButtonTest';
import { ZoomTestSimple } from './Commun/components/ZoomTestSimple';
import { globalErrorHandler } from './Commun/utils/GlobalErrorHandler';
import { ApplicationProvider, useApplication } from './Commun/contexts/ApplicationContext';
import { useParams, useLocation } from 'react-router-dom';
import { MenuProvider } from './Commun/contexts/MenuContext';
import { ThemeProvider } from './Commun/contexts/ThemeContext';
import { NotificationDataSync } from './Commun/components/NotificationDataSync';
// boubaker 23/08/2026 - hôte + point d'entrée unique des messages génériques
import { BlobticMessageHost, blobticMessage } from './Commun/components/BlobticMessage';
import { BlobticLoadingHost } from './Commun/components/BlobticLoadingOverlay';
import { ChatProvider } from './Commun/contexts/ChatContext';
import { SystemParametersProvider } from './Commun/contexts/SystemParametersContext';
import { StudentPreRegistrationPage } from './Academy/pages/StudentPreRegistrationPage';
import { AcademyQuizPage } from './Academy/pages/AcademyQuizPage';
import SsoCallbackPage from './Commun/pages/SsoCallbackPage';
import StudentPortalApp from './Academy/portal/student/StudentPortalApp';
import TeacherPortalApp from './Academy/portal/teacher/TeacherPortalApp';
import PortalSsoCallbackPage from './Academy/portal/common/PortalSsoCallbackPage';

// Composant de protection des routes
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const { appName } = useParams<{ appName: string }>();
  const { isValidApp } = useApplication();
  const { clearTabs } = useTabContext();
  const [switching, setSwitching] = React.useState(false);

  // If authenticated but visiting a route for a DIFFERENT application, close old connection and redirect
  React.useEffect(() => {
    if (isAuthenticated && appName && isValidApp(appName) && user?.appName) {
      if (user.appName.toLowerCase() !== appName.toLowerCase()) {
        setSwitching(true);
        logout(true).then(() => {
          clearTabs();
          setSwitching(false);
        });
      }
    }
  }, [isAuthenticated, appName, isValidApp, user?.appName, logout, clearTabs]);

  if (isLoading || switching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    const targetApp = appName && isValidApp(appName) ? appName : 'Planning';
    return <Navigate to={`/${targetApp}/login`} replace />;
  }

  // App mismatch protection
  if (appName && isValidApp(appName) && user?.appName && user.appName.toLowerCase() !== appName.toLowerCase()) {
    return <Navigate to={`/${appName}/login`} replace />;
  }

  return <>{children}</>;
};

// Composant pour wrapper les routes avec la validation de l'app
const AppRouteWrapper: React.FC = () => {
  const { setApp, isValidApp } = useApplication();
  const location = useLocation();
  const params = useParams<{ appName: string; }>();

  React.useEffect(() => {
    // Extract appName from path if possible (e.g. /Planning/dashboard)
    // This might be redundant if using params from a Route, but useful for initial load detection logic
    const pathSegments = location.pathname.split('/');
    const potentialApp = pathSegments[1]; // index 0 is empty string
    if (potentialApp && isValidApp(potentialApp)) {
      setApp(potentialApp as import('./Commun/contexts/ApplicationContext').ApplicationName);
    }
  }, [location, setApp, isValidApp]);

  return (
    <Routes>
      <Route path="/" element={<RedirectToExternal />} />
      <Route path="/student-pre-registration" element={<StudentPreRegistrationPage />} />
      <Route path="/academy-quiz" element={<AcademyQuizPage />} />
      <Route path="/portal/student/*" element={<StudentPortalApp />} />
      <Route path="/portal/student" element={<StudentPortalApp />} />
      <Route path="/portal/teacher/*" element={<TeacherPortalApp />} />
      <Route path="/portal/teacher" element={<TeacherPortalApp />} />
      <Route path="/portal/sso/callback" element={<PortalSsoCallbackPage />} />
      {/* <Route path="/" element={<Navigate to="/Planning/login" replace />} /> */}
      <Route
        path="/:appName/login"
        element={
          <AppLoginWrapper />
        }
      />
      <Route
        path="/:appName/auth/sso/callback"
        element={<SsoCallbackPage />}
      />

      {/* All other routes are handled by Layout */}
      <Route
        path="/:appName/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/:appName/security-web"
        element={
          <ProtectedRoute>
            <Layout>
              <SecurityWebPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback for unknown routes - redirect to Planning login */}
      <Route path="*" element={<Navigate to="/Planning/login" replace />} />
    </Routes>
  );
};

const AppLoginWrapper: React.FC = () => {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const { appName } = useParams<{ appName: string }>();
  const { isValidApp, setApp } = useApplication();
  const { clearTabs } = useTabContext();
  const [switching, setSwitching] = React.useState(false);

  React.useEffect(() => {
    if (appName && isValidApp(appName)) {
      setApp(appName as any);
    }
  }, [appName, isValidApp, setApp]);

  // Handle application switching if already authenticated or token exists for a different app
  React.useEffect(() => {
    if (!appName || !isValidApp(appName)) return;

    const storedApp = sessionStorage.getItem('blobtic_app_name');
    const storedToken = sessionStorage.getItem('blobtic_token');
    const activeApp = user?.appName || storedApp;

    // If an active session or token belongs to a DIFFERENT application, close it!
    if (storedToken && activeApp && activeApp.toLowerCase() !== appName.toLowerCase()) {
      setSwitching(true);
      logout(true).then(() => {
        clearTabs();
        setSwitching(false);
      });
    }
  }, [appName, isValidApp, user?.appName, logout, clearTabs]);

  if (!appName || !isValidApp(appName)) {
    return <Navigate to="/Planning/login" replace />;
  }

  if (isLoading || switching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Only redirect to dashboard if authenticated AND the active session's application matches this app
  const isMatch = user?.appName ? user.appName.toLowerCase() === appName.toLowerCase() : false;
  if (isAuthenticated && isMatch) {
    return <Navigate to={`/${appName}/dashboard`} replace />;
  }

  return <LoginPage />;
};

const RedirectToExternal: React.FC = () => {
  React.useEffect(() => {
    window.location.href = '/Externel_apps/index.html';
  }, []);
  return null;
};

const AppContent: React.FC = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppRouteWrapper />
    </Box>
  );
};

// Composant pour enregistrer les gestionnaires globaux
const GlobalHandlerRegistrar: React.FC = () => {
  const { logout } = useAuth();

  React.useEffect(() => {
    // Enregistrer la fonction de logout
    globalErrorHandler.registerLogout(() => {
      logout();
    });

    // boubaker 23/08/2026 - notificateur global branché sur blobticMessage (point d'entrée unique)
    globalErrorHandler.registerNotifier((title, message, type) => {
      switch (type) {
        case 'error': blobticMessage.error(title, message); break;
        case 'success': blobticMessage.success(title, message); break;
        case 'warning': blobticMessage.warning(title, message); break;
        case 'info': blobticMessage.info(title, message); break;
      }
    });
  }, [logout]);

  return null;
};





// ...

// Composant racine avec providers
const App: React.FC = () => {
  const isPublicRoute = typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/student-pre-registration') || window.location.pathname.startsWith('/academy-quiz'));

  if (isPublicRoute) {
    return (
      <LanguageProvider>
        <ThemeProvider>
          <CssBaseline />
          <Router>
            <Routes>
              <Route path="/student-pre-registration" element={<StudentPreRegistrationPage />} />
              <Route path="/academy-quiz" element={<AcademyQuizPage />} />
              <Route path="*" element={<Navigate to="/student-pre-registration" replace />} />
            </Routes>
          </Router>
        </ThemeProvider>
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <ThemeProvider>
        <CssBaseline />
        <SystemParametersProvider>
          <NotificationProvider>
            <AuthProvider>
              <ChatProvider>
                <NotificationDataSync />
                <Router>
                  <ApplicationProvider>
                    <TabProvider>
                      <MenuProvider>
                        <GlobalHandlerRegistrar />
                        <AppContent />
                        {/* boubaker 23/08/2026 - hôte des messages génériques (confirm/alert/toasts animés) */}
                        <BlobticMessageHost />
                        <BlobticLoadingHost />
                      </MenuProvider>
                    </TabProvider>
                  </ApplicationProvider>
                </Router>
              </ChatProvider>
            </AuthProvider>
          </NotificationProvider>
        </SystemParametersProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
};

export default App;
